/* eslint-disable max-lines */
/* eslint-disable max-lines-per-function */
/* eslint-disable function-call-argument-newline */
import dayjs from 'dayjs';
import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  FormControl,
  MenuItem,
  OutlinedInput,
  Select as MuiSelect,
} from '@mui/material';
import { Calendar } from 'primereact/calendar';

import UseAxios from '../../../Api/http/http';
import dropdwn from '../../../Assets/images/svg/dar_dropdown_arrow.svg';
import {
  getMilestoneDropdownOptions,
  createCollection,
  updateCollection,
  getSingleCollection,
  getSingleInvoice,
} from '../../../Api/request/milestone';

const resolveZohoClientName = (source = {}) => (
  source?.zoho_customer_name
  || source?.zohoCustomerName
  || source?.parentZohoCustomerName
  || source?.parentZohoCustomer
  || source?.milestone_zoho_customer_name
  || source?.client_name
  || ''
);

/* -------------------------------------------------------------------------- */
/*  Date helpers                                                               */
/* -------------------------------------------------------------------------- */

const MONTH_ABBR = {
  Jan: '01',
  Feb: '02',
  Mar: '03',
  Apr: '04',
  May: '05',
  Jun: '06',
  Jul: '07',
  Aug: '08',
  Sep: '09',
  Oct: '10',
  Nov: '11',
  Dec: '12',
};

const parseDateForInput = (dateVal) => {
  if (!dateVal || dateVal === '-' || dateVal === '9999-01-01' || dateVal === '0000-00-00') return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateVal)) return dateVal;
  const match = /^(\d{1,2})-([A-Za-z]{3})-(\d{4})$/.exec(dateVal);
  if (match) return `${match[3]}-${MONTH_ABBR[match[2]] || '01'}-${match[1].padStart(2, '0')}`;
  return '';
};

/* -------------------------------------------------------------------------- */
/*  Constants                                                                  */
/* -------------------------------------------------------------------------- */

const EMPTY_COLLECTION = {
  amountToCollect: '',
  collectionDueDate: '',
  collectionProjectionDate: '',
  collectionDate: '',
  collectedAmount: '',
  collectionCommentId: '',
  collectionRemarks: '',
  paymentStatusId: '',
};

/* -------------------------------------------------------------------------- */
/*  CollectionFormDialog                                                       */
/* -------------------------------------------------------------------------- */
const CollectionFormDialog = ({
  visible,
  onHide,
  mode,
  invoiceRow,
  rowData,
  token,
  onSuccess,
}) => {
  const [form, setForm] = useState(EMPTY_COLLECTION);
  const [ctx, setCtx] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [collectionComments, setCollectionComments] = useState([]);
  const [paymentStatuses, setPaymentStatuses] = useState([]);
  const [showTdsPopover, setShowTdsPopover] = useState(false);
  const [tdsPercent, setTdsPercent] = useState('');

  useEffect(() => {
    if (!visible) return;
    setError('');
    setShowTdsPopover(false);
    setTdsPercent('');
    setCtx(null);

    const editCollectionId = rowData?.collectionId ?? rowData?.collection_id;
    const isEdit = mode === 'edit' && editCollectionId;

    const addInvoiceId = invoiceRow?.invoiceId ?? invoiceRow?.invoice_id;
    const shouldFetchInvoiceForAdd = mode === 'add' && addInvoiceId;

    if (isEdit) setLoading(true);

    Promise.all([
      UseAxios(getMilestoneDropdownOptions('collection-comments')),
      UseAxios(getMilestoneDropdownOptions('payment-statuses')),
      isEdit ? UseAxios(getSingleCollection(editCollectionId)) : Promise.resolve(null),
      shouldFetchInvoiceForAdd ? UseAxios(getSingleInvoice(addInvoiceId)) : Promise.resolve(null),
    ]).then(([ccRes, psRes, collRes, addInvoiceRes]) => {
      setCollectionComments(ccRes?.data?.data || []);
      setPaymentStatuses(psRes?.data?.data || []);

      if (isEdit && collRes) {
        const data = collRes?.data?.data || {};
        setCtx(data);
        setForm({
          amountToCollect: data.amount_to_collect ?? '',
          collectionDueDate: parseDateForInput(data.collection_due_date),
          collectionProjectionDate: parseDateForInput(data.collection_projection_date),
          collectionDate: parseDateForInput(data.collection_date),
          collectedAmount: data.collected_amount ?? '',
          collectionCommentId: data.collection_comment_id ? String(data.collection_comment_id) : '',
          collectionRemarks: data.collection_remarks || '',
          paymentStatusId: data.payment_status_id ? String(data.payment_status_id) : '',
        });
        setLoading(false);
      } else {
        setForm(EMPTY_COLLECTION);
        if (mode === 'add' && invoiceRow) {
          setCtx({
            po_number: invoiceRow.parentPoNumber ?? '',
            milestone_id: invoiceRow.parentMilestoneId ?? '',
            milestone_status_name: invoiceRow.parentMilestoneStatus ?? '',
            milestone_type_id: invoiceRow.parentMilestoneTypeId ?? null,
            milestone_type_name: invoiceRow.parentMilestoneType ?? '',
            invoice_status_name: invoiceRow.parentInvoiceStatus ?? '',
            milestone: invoiceRow.parentMilestone ?? '',
            category: invoiceRow.parentCategory ?? '',
            cont_value_p: invoiceRow.parentRawContValueP ?? null,
            amount: invoiceRow.parentFormattedAmount ?? '',
            due_date: invoiceRow.parentRawDueDate ?? null,
            replanned_due_date: invoiceRow.parentRawReplannedDueDate ?? null,
            m_remarks: invoiceRow.m_remarks
              ?? invoiceRow.parentMilestoneRemarks
              ?? invoiceRow.parentRemarks
              ?? '',
            im_invoice_id: invoiceRow.invoiceId ?? '',
            zoho_invoice_number: invoiceRow.zohoInvoiceNumber ?? '',
            zoho_customer_name: (
              resolveZohoClientName(invoiceRow)
              || resolveZohoClientName(addInvoiceRes?.data?.data || {})
            ),
            invoice_amount: invoiceRow.rawInvoiceAmount ?? null,
            invoice_amount_with_tax: invoiceRow.rawInvoiceAmountWithTax ?? null,
            invoice_date: invoiceRow.rawInvoiceDate ?? null,
            invoice_type_name: invoiceRow.invoiceType ?? '',
            credit_note_linked_id: invoiceRow.creditNoteLinkedId ?? null,
            revenue_date: invoiceRow.rawRevenueDate ?? null,
            invoice_remarks: invoiceRow.remarks ?? '',
            billed_effort: invoiceRow.billedEffort !== ''
              ? Number(invoiceRow.billedEffort) : null,
            collection_credit_period: invoiceRow.collectionCreditPeriod !== ''
              ? Number(invoiceRow.collectionCreditPeriod) : null,
          });
        }
      }
    });
  }, [visible, mode, rowData, invoiceRow]);

  if (!visible) return null;

  const setField = (key, val) => setForm((prev) => ({ ...prev, [key]: val }));

  const applyTds = () => {
    const pct = parseFloat(tdsPercent);
    const base = parseFloat(form.amountToCollect);
    if (Number.isFinite(pct) && Number.isFinite(base)) {
      setField('collectedAmount', String((base - (pct / 100) * base).toFixed(2)));
    }
    setShowTdsPopover(false);
    setTdsPercent('');
  };

  const handleSubmit = async () => {
    if (!form.amountToCollect) {
      setError('Amount to Collect is required.');
      return;
    }
    setSubmitting(true);
    setError('');

    const invoiceId = invoiceRow?.invoice_id || invoiceRow?.invoiceId
      || rowData?.parentInvoiceId || rowData?.invoice_id;

    const payload = {
      invoiceId,
      amountToCollect: form.amountToCollect,
      collectionDueDate: form.collectionDueDate,
      collectionProjectionDate: form.collectionProjectionDate,
      collectionDate: form.collectionDate,
      collectedAmount: form.collectedAmount,
      collectionCommentId: form.collectionCommentId,
      collectionRemarks: form.collectionRemarks,
      paymentStatusId: form.paymentStatusId,
      invoiceAmountWithTax: (
        invoiceRow?.invoice_amount_with_tax
        || invoiceRow?.invoiceAmountWithTax
        || ctx?.invoice_amount_with_tax
        || 0
      ),
    };

    const req = mode === 'edit'
      ? updateCollection(rowData.collectionId ?? rowData.collection_id, payload)
      : createCollection(invoiceId, payload);

    const result = await UseAxios({ ...req, headers: { ...req.headers, token } });
    setSubmitting(false);

    if (result?.data?.success) {
      onSuccess?.(mode === 'edit' ? 'Collection updated successfully.' : 'Collection added successfully.');
      onHide();
    } else {
      setError(result?.data?.message || 'Operation failed. Please try again.');
    }
  };

  /* ---------- field helper (same pattern as InvoiceFormDialog) ---------- */
  const field = (label, required, input) => (
    <div className="ms-dialog__field">
      <label className="ms-dialog__label">
        {label}
        {required && <span className="ms-dialog__required"> *</span>}
      </label>
      {input}
    </div>
  );

  const rdOnly = (val) => (
    <input
      type="text"
      className="ms-dialog__input ms-dialog__input--readonly"
      value={val != null ? String(val) : ''}
      readOnly
    />
  );

  const renderSelectValue = (selectedValue, options) => {
    if (!selectedValue) {
      return <span className="ms-dialog__placeholder">Select</span>;
    }
    const match = options.find((item) => String(item.id) === String(selectedValue));
    return match ? match.label : selectedValue;
  };

  const renderSelectField = (value, options, onChange) => (
    <div className="ms-dialog__select-wrap">
      <FormControl fullWidth size="small">
        <MuiSelect
          displayEmpty
          value={value}
          onChange={onChange}
          input={<OutlinedInput className="ms-dialog__input ms-dialog__input--mui" />}
          renderValue={(selectedValue) => renderSelectValue(selectedValue, options)}
          IconComponent={() => null}
          MenuProps={{
            disablePortal: false,
            sx: {
              zIndex: 9105,
            },
            MenuListProps: {
              sx: {
                maxHeight: 181,
                overflowY: 'auto',
                paddingTop: 0,
                paddingBottom: 0,
              },
            },
            PaperProps: {
              className: 'ms-dialog__menu-paper',
              sx: {
                overflow: 'hidden',
              },
            },
          }}
        >
          <MenuItem value="">
            <span className="ms-dialog__placeholder">Select</span>
          </MenuItem>
          {options.map((item) => (
            <MenuItem key={item.id} value={String(item.id)}>
              {item.label}
            </MenuItem>
          ))}
        </MuiSelect>
      </FormControl>
      <img src={dropdwn} alt="dropdown" className="ms-dialog__select-icon" />
    </div>
  );

  const renderDateField = (value, keyName, placeholder = 'dd-mm-yyyy') => (
    <div className="ms-dialog__date-picker">
      <Calendar
        value={value ? new Date(value) : null}
        onChange={(val) => {
          const newValue = val.value;
          if (!newValue) {
            setField(keyName, '');
            return;
          }
          setField(keyName, dayjs(newValue).format('YYYY-MM-DD'));
        }}
        dateFormat="dd-mm-yy"
        placeholder={placeholder}
        disabled={submitting || loading}
        inputClassName="ms-dialog__input"
        style={{ width: '100%' }}
      />
    </div>
  );

  const rdDate = (val, placeholder = 'dd-mm-yyyy') => (
    <div className="ms-dialog__date-picker">
      <Calendar
        value={val ? new Date(parseDateForInput(val)) : null}
        onChange={() => {}}
        dateFormat="dd-mm-yy"
        placeholder={placeholder}
        disabled
        inputClassName="ms-dialog__input ms-dialog__input--readonly"
        style={{ width: '100%' }}
      />
    </div>
  );

  /* context values (filled in edit mode from API) */
  const ctxPoNumber = ctx?.po_number || '';
  const ctxMilestoneId = ctx?.milestone_id || '';
  const ctxMilestoneStatus = ctx?.milestone_status_name || '';
  const ctxMilestoneType = ctx?.milestone_type_name || '';
  const ctxInvoiceStatus = ctx?.invoice_status_name || '';

  const ctxMilestone = ctx?.milestone || '';
  const ctxCategory = ctx?.category || '';
  const ctxContValueP = ctx?.cont_value_p != null ? `${ctx.cont_value_p}%` : '';
  const ctxAmount = ctx?.amount || '';

  const ctxDueDate = ctx?.due_date || '';
  const ctxReplannedDate = ctx?.replanned_due_date || '';
  const ctxMilestoneRemarks = ctx?.m_remarks || ctx?.milestone_remarks || '';
  const ctxZohoClientName = resolveZohoClientName(ctx || {});

  const ctxInvoiceId = ctx?.im_invoice_id || ctx?.invoice_id || '';
  const ctxZohoNo = ctx?.zoho_invoice_number || '';
  const ctxInvAmount = ctx?.invoice_amount != null ? String(ctx.invoice_amount) : '';
  const ctxInvWithTax = ctx?.invoice_amount_with_tax != null ? String(ctx.invoice_amount_with_tax) : '';

  const ctxInvDate = ctx?.invoice_date || '';
  const ctxInvType = ctx?.invoice_type_name || '';
  const ctxCreditNoteId = ctx?.credit_note_linked_id != null ? String(ctx.credit_note_linked_id) : '';
  const ctxRevenueDate = ctx?.revenue_date || '';

  const ctxRemarks = ctx?.invoice_remarks || '';
  const ctxBilledEffort = ctx?.billed_effort != null ? String(ctx.billed_effort) : '';
  const ctxCreditPeriod = ctx?.collection_credit_period != null
    ? String(ctx.collection_credit_period) : '';

  return (
    <div className="ms-dialog-overlay" role="dialog" aria-modal="true">
      <div className="ms-dialog ms-dialog--xl ms-dialog--collection-form">
        <div className="ms-dialog__header">
          {mode === 'edit' ? 'Edit Invoice Collection' : 'Add Collection'}
        </div>

        {loading ? (
          <div className="ms-dialog__loading">Loading...</div>
        ) : (
          <div className="ms-dialog__form ms-dialog__form--4col ms-dialog__form--collection">

            {/*   Row 1: PO / Milestone header   */}
            {field('PO Number', false, rdOnly(ctxPoNumber))}
            {field('Milestone Id', false, rdOnly(String(ctxMilestoneId)))}
            {field('Milestone Status', false, rdOnly(ctxMilestoneStatus))}
            {field('Milestone Type', false, rdOnly(ctxMilestoneType))}
            {field('Invoice Status', false, rdOnly(ctxInvoiceStatus))}

            {/*   Row 2: Milestone details   */}
            {field('Milestone', false, rdOnly(ctxMilestone))}
            {field('Category', false, rdOnly(ctxCategory))}
            {field('Zoho Client Name', false, rdOnly(ctxZohoClientName))}
            {field('% (Of Contract Value)', false, rdOnly(ctxContValueP))}
            {field('Due Amount (INR)', false, rdOnly(String(ctxAmount)))}

            {/*   Row 3: Milestone dates   */}
            {field('Due Date', false, rdDate(ctxDueDate))}
            {field('Replanned Due Date', false, rdDate(ctxReplannedDate))}
            {field('Milestone Remarks', false, rdOnly(ctxMilestoneRemarks))}

            <div className="ms-dialog__section-divider" aria-hidden="true" />

            {/*   Row 4: Invoice   */}
            {field('Invoice ID', false, rdOnly(String(ctxInvoiceId)))}
            {field('Zoho Invoice Number', false, rdOnly(ctxZohoNo))}
            {field('Invoice Amount (INR)', false, rdOnly(ctxInvAmount))}
            {field('Invoice Amount with Tax (INR)', false, rdOnly(ctxInvWithTax))}

            {/*   Row 5: Invoice cont'd   */}
            {field('Invoice Date', false, rdDate(ctxInvDate))}
            {field('Invoice Type', false, rdOnly(ctxInvType))}
            {field('Invoice Id Related to Credit Note', false, rdOnly(ctxCreditNoteId))}
            {field('Revenue Recognition Date', false, rdDate(ctxRevenueDate))}

            {/*   Row 6: Invoice cont'd + editable Payment Status   */}
            {field('Remarks', false, rdOnly(ctxRemarks))}
            {field('Billed Effort (hrs)', false, rdOnly(ctxBilledEffort))}

            {field('Payment Status', true, renderSelectField(
              form.paymentStatusId,
              paymentStatuses,
              (ev) => {
                setField('paymentStatusId', ev.target.value);
              },
            ))}

            {field('Collection Credit Period (Days)', false, rdOnly(ctxCreditPeriod))}

            <div className="ms-dialog__section-divider" aria-hidden="true" />

            {/*   Row 7: Editable collection fields   */}
            {field('Amount To Collect (INR)', true,
              <input
                type="number"
                className="ms-dialog__input"
                placeholder="Enter Amount To Collect"
                value={form.amountToCollect}
                onChange={(ev) => {
                  setField('amountToCollect', ev.target.value);
                }}
              />,
            )}

            {field('Collection Due Date', true, renderDateField(form.collectionDueDate, 'collectionDueDate'))}
            {field(
              'Collection Projection Date',
              true,
              renderDateField(form.collectionProjectionDate, 'collectionProjectionDate'),
            )}
            {field('Collected Date', true, renderDateField(form.collectionDate, 'collectionDate'))}

            {/*   Row 8   */}
            <div className="ms-dialog__field ms-dialog__field--tds-wrap">
              <label className="ms-dialog__label">
                Collected Amount (INR) <span className="ms-dialog__required">*</span>
              </label>
              <div className="ms-tax-input-row">
                <input
                  type="number"
                  className="ms-dialog__input"
                  placeholder="Enter Collected Amount (INR)"
                  value={form.collectedAmount}
                  onChange={(ev) => {
                    setField('collectedAmount', ev.target.value);
                  }}
                />
              </div>
              <div className="ms-tax-trigger-row">
                <button
                  type="button"
                  className="ms-tax-btn"
                  onClick={() => {
                    setShowTdsPopover((prev) => !prev);
                  }}
                >
                  TDS %
                </button>
              </div>

              {showTdsPopover && (
                <div className="ms-tax-popover">
                  <div className="ms-tax-popover__title-row">
                    <div className="ms-tax-popover__title">Add TDS %</div>
                    <div className="ms-tax-popover__info-wrap">
                      <button
                        type="button"
                        className="ms-tax-popover__icon-btn"
                        aria-label="TDS formula"
                      >
                        <span className="ms-tax-popover__icon">i</span>
                      </button>
                      <div className="ms-tax-popover__formula-bubble">
                        Collected amount = Amount to collect - ((TDS % / 100) * Amount to collect)
                      </div>
                    </div>
                  </div>
                  <input
                    type="number"
                    className="ms-dialog__input"
                    placeholder="Enter TDS %"
                    value={tdsPercent}
                    onChange={(ev) => {
                      setTdsPercent(ev.target.value);
                    }}
                  />
                  <div className="ms-tax-popover__actions">
                    <button
                      type="button"
                      className="ms-btn ms-btn--ghost"
                      onClick={() => {
                        setShowTdsPopover(false);
                        setTdsPercent('');
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="ms-btn ms-btn--primary"
                      onClick={applyTds}
                    >
                      Add
                    </button>
                  </div>
                </div>
              )}
            </div>

            {field('Collection Comment', true, renderSelectField(
              form.collectionCommentId,
              collectionComments,
              (ev) => {
                setField('collectionCommentId', ev.target.value);
              },
            ))}

            {field('Collection Remarks', true,
              <input
                type="text"
                className="ms-dialog__input"
                placeholder="Enter..."
                value={form.collectionRemarks}
                onChange={(ev) => {
                  setField('collectionRemarks', ev.target.value);
                }}
              />,
            )}

            <div className="ms-dialog__field" />
          </div>
        )}

        {error && <div className="ms-dialog__error">{error}</div>}

        <div className="ms-dialog__footer">
          <button
            type="button"
            className="ms-btn ms-btn--ghost"
            onClick={onHide}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="button"
            className="ms-btn ms-btn--primary"
            onClick={handleSubmit}
            disabled={submitting || loading}
          >
            {submitting
              ? (mode === 'edit' ? 'Updating...' : 'Adding...')
              : (mode === 'edit' ? 'Update' : 'Add')}
          </button>
        </div>
      </div>
    </div>
  );
};

CollectionFormDialog.propTypes = {
  visible: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  mode: PropTypes.oneOf(['add', 'edit']).isRequired,
  invoiceRow: PropTypes.object,
  rowData: PropTypes.shape({
    collection_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    collectionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    invoice_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    parentInvoiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  token: PropTypes.string,
  onSuccess: PropTypes.func,
};

CollectionFormDialog.defaultProps = {
  invoiceRow: null,
  rowData: null,
  token: '',
  onSuccess: null,
};

export default CollectionFormDialog;
